/**
 * `participantTransformConversion.ts` — the three.js-typed adapter for the
 * WORLD-POSE half of the Unity ↔ three basis change.
 *
 * All the math lives in `@models/utils/unityBasis`; this file only puts
 * `Vector3` / `Quaternion` types on it. Do not re-derive a conversion here.
 *
 * Used by anything that exchanges Unity WORLD coordinates: participant poses
 * and their lasers, the follow camera, the local camera pose published every
 * frame, media tiles / cards, sketch capture poses, surface placement, and the
 * annotation marker layer. `ParticipantTransform` payloads stay in Unity world
 * space inside `MultiuserSessionModel` (the same wire shape as Unity's
 * `ParticipantTransformData`); the conversion happens here, at the renderer
 * boundary, mirroring how `snapshotRotationConversion.ts` treats entity
 * snapshots.
 *
 * ## Positions and rotations do NOT use the same rule here
 *
 * - Positions and directions take the plain canonical map `M = diag(-1, 1, 1)`.
 * - Rotations take `σ(R) · Ry(180)` — the basis conjugation PLUS a
 *   forward-convention adapter, because Unity treats an object's local **+Z**
 *   as forward while three's cameras look down local **−Z**, and every mesh
 *   wrapper in this port (avatar heads, device frames, cards, name tags) is
 *   authored to that −Z convention.
 *
 * That asymmetry is not a hack; it is two genuinely different questions being
 * answered. It only became visible with task 0004. Before it, the port's world
 * map was `diag(-1, 1, -1)` — a proper ROTATION — and `diag(-1,1,-1)·ez = -ez`
 * happened to supply the forward adapter for free, so a single conjugation
 * appeared to do both jobs. The canonical `M` does not: `M·ez = +ez`. Moving the
 * map without re-adding the adapter turns every camera, HMD head, remote avatar
 * and non-billboard card 180° around — and because the orbit target sits on the
 * Y axis, the result looks plausible rather than broken.
 *
 * ## Do not use these to convert a RELATIVE rotation
 *
 * `convertWorldPoseQuaternion` is self-inverse but is NOT a homomorphism (the
 * trailing `Ry(180)` does not cancel across a product). Convert whole world
 * poses at the boundary. For a node-local rotation, or anything you intend to
 * compose, use `snapshotRotationConversion.ts` / `convertBasisQuaternion`,
 * which is a true conjugation.
 */

import { Quaternion, Vector3 } from "three";
import type {
  ParticipantQuat,
  ParticipantVec3,
} from "@models/multiuser/IMultiuserSessionModel";
import {
  convertBasisPosition,
  convertWorldPoseQuaternion,
} from "@models/utils/unityBasis";

export function unityWorldPositionToThree(p: ParticipantVec3): Vector3 {
  const [x, y, z] = convertBasisPosition(p);
  return new Vector3(x, y, z);
}

export function unityWorldQuaternionToThree(q: ParticipantQuat): Quaternion {
  const [x, y, z, w] = convertWorldPoseQuaternion(q);
  return new Quaternion(x, y, z, w);
}

export function threeWorldPositionToUnity(p: Vector3): ParticipantVec3 {
  const [x, y, z] = convertBasisPosition([p.x, p.y, p.z]);
  return [x, y, z];
}

export function threeWorldQuaternionToUnity(q: Quaternion): ParticipantQuat {
  return convertWorldPoseQuaternion([q.x, q.y, q.z, q.w]) as ParticipantQuat;
}
